import type { Router } from "vue-router";

// After a frontend deploy, Vite emits new content-hashed chunk filenames and
// the old ones are removed from the server. A browser still running the
// pre-deploy shell then fails to import a lazy chunk ("Failed to fetch
// dynamically imported module"), leaving pages blank or links dead. The fix is
// to reload once so the browser fetches the new index.html and the new chunks.
// See netfm-company/runbooks/spa-stale-chunk-after-deploy.md.
//
// On Vite 3 there is no `vite:preloadError` event, so we catch the failure two
// ways: Vue Router's onError (lazy route components) and a global
// unhandledrejection listener (lazy imports that aren't route components).

export const STALE_CHUNK_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

const RELOAD_FLAG = "staleChunkReloading";

function reloadOnce(target?: string) {
  // Guard against a reload loop if the chunk is genuinely gone (a broken build
  // rather than a stale client). Re-armed by router.afterEach on success.
  if (sessionStorage.getItem(RELOAD_FLAG)) return;
  sessionStorage.setItem(RELOAD_FLAG, "1");
  if (target) window.location.assign(target);
  else window.location.reload();
}

export function recoverFromStaleChunks(router: Router) {
  router.onError((error, to) => {
    if (STALE_CHUNK_RE.test(error?.message ?? "")) {
      reloadOnce(to.fullPath);
    }
  });

  // A successful navigation means the current build is healthy, so re-arm the
  // guard for the next deploy.
  router.afterEach(() => sessionStorage.removeItem(RELOAD_FLAG));

  window.addEventListener("unhandledrejection", (event) => {
    const message =
      event.reason instanceof Error
        ? event.reason.message
        : String(event.reason ?? "");
    if (STALE_CHUNK_RE.test(message)) {
      reloadOnce();
    }
  });
}
